import React, { useState } from "react";
import { Upload, CheckCircle2 } from "lucide-react";
import { readSheet } from "read-excel-file/browser";
import {
  importColumns,
  guessMapping,
  parseCsv,
  mapRows,
  analyzeImport,
  importProspects,
} from "../services/import.mjs";
import { Field, Select, Empty } from "../components/ui";
export default function ImportProspects({ state, onApply }) {
  const [rows, setRows] = useState([]),
    [mapping, setMapping] = useState({}),
    [required, setRequired] = useState(["siret", "name"]),
    [strict, setStrict] = useState(false),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [stats, setStats] = useState(null),
    [fileName, setFileName] = useState(""),
    [owner, setOwner] = useState(""),
    [agency, setAgency] = useState("");
  const options = {
    required,
    strict,
    ...(owner ? { owner } : {}),
    ...(agency ? { agency } : {}),
  };
  const mapped = mapRows(rows, mapping),
    preview = analyzeImport(state, mapped, options),
    accepted = preview.filter((r) => !r.error).length;
  async function load(file) {
    if (!file) return;
    setBusy(true);
    setError("");
    setStats(null);
    try {
      if (file.size > 10 * 1024 * 1024)
        throw new Error("Fichier limité à 10 Mo dans ce prototype.");
      let parsed;
      if (/\.csv$/i.test(file.name)) parsed = parseCsv(await file.text());
      else if (/\.xlsx$/i.test(file.name)) parsed = await readSheet(file);
      else
        throw new Error(
          "Choisis un fichier .csv ou .xlsx (convertir les anciens .xls en .xlsx).",
        );
      if (parsed.length < 2)
        throw new Error(
          "Le fichier doit contenir des en-têtes et au moins une ligne.",
        );
      setRows(parsed);
      setMapping(guessMapping(parsed[0]));
      setFileName(file.name);
    } catch (e) {
      setRows([]);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <p className="muted">
        Importer, vérifier puis enrichir la base unique d’établissements. Les
        clients connus conservent leur fiche et leur historique.
      </p>
      <div className="importUpload">
        <Upload />
        <label className="field">
          Fichier Excel / CSV
          <input
            type="file"
            accept=".csv,.xlsx"
            aria-label="Fichier Excel ou CSV"
            onChange={(e) => load(e.target.files[0])}
            disabled={busy}
          />
        </label>
        <a className="textAction" href="/modele-prospection.csv" download>
          Télécharger le modèle et le scénario EHPAD
        </a>
      </div>
      <p className="hint">
        Les exemples sont fictifs. Stocker les SIRET et téléphones comme texte
        dans Excel pour conserver les zéros initiaux. Le statut « fermé »
        provient du fichier ou des données locales, sans interrogation SIRENE.
      </p>
      {busy && <p role="status">Lecture du fichier…</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {!!rows.length && (
        <>
          <h3>
            {fileName} · {mapped.length} lignes
          </h3>
          <details className="importOptions" open>
            <summary>Correspondance des colonnes et minimum requis</summary>
            <div className="formGrid">
              {Object.entries(importColumns).map(([field, label]) => (
                <Select
                  key={field}
                  label={label}
                  value={mapping[field] ?? ""}
                  onChange={(e) => {
                    setStats(null);
                    setMapping((m) => ({ ...m, [field]: e.target.value }));
                  }}
                >
                  <option value="">Non fourni</option>
                  {rows[0].map((header, i) => (
                    <option key={i} value={i}>
                      {String(header || `Colonne ${i + 1}`)}
                    </option>
                  ))}
                </Select>
              ))}
            </div>
            <h3>Champs minimum requis</h3>
            <div className="taskFilters">
              {[
                "siret",
                "name",
                "city",
                "job",
                "source",
                "date",
                "contactName",
              ].map((field) => (
                <label className="check" key={field}>
                  <input
                    type="checkbox"
                    checked={required.includes(field)}
                    disabled={field === "siret"}
                    onChange={(e) =>
                      setRequired((r) =>
                        e.target.checked
                          ? [...r, field]
                          : r.filter((x) => x !== field),
                      )
                    }
                  />
                  {importColumns[field]}
                </label>
              ))}
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={strict}
                onChange={(e) => setStrict(e.target.checked)}
              />
              Contrôler aussi la clé SIRET (sinon : format 14 chiffres ; mode
              adapté aux identifiants fictifs de démonstration)
            </label>
            <div className="formGrid">
              <Field
                label="Commercial responsable des nouveaux prospects (optionnel)"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
              />
              <Field
                label="Agence des nouveaux prospects (optionnelle)"
                value={agency}
                onChange={(e) => setAgency(e.target.value)}
              />
            </div>
          </details>
          <div className="tableScroll">
            <table className="taskTable">
              <thead>
                <tr>
                  <th>Ligne</th>
                  <th>SIRET</th>
                  <th>Établissement</th>
                  <th>Traitement</th>
                  <th>Vérification</th>
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 200).map((item) => (
                  <tr key={item.line}>
                    <td>{item.line}</td>
                    <td>{item.row.siret || "—"}</td>
                    <td>{item.row.name}</td>
                    <td>
                      <span className="status">{item.action}</span>
                    </td>
                    <td>{item.error || "Nouveau signal commercial"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.length > 200 && (
            <p className="hint">
              Aperçu limité aux 200 premières lignes ; toutes les lignes seront
              traitées.
            </p>
          )}
          <button
            className="primary wide"
            disabled={!accepted || busy || !!stats}
            onClick={() => {
              try {
                const result = importProspects(state, mapped, options);
                onApply(result.state);
                setStats(result.stats);
                setError("");
              } catch (e) {
                setError(e.message);
              }
            }}
          >
            <CheckCircle2 />
            Importer {accepted} lignes acceptées
          </button>
          <p className="hint">
            Les lignes en erreur sont exclues. Un SIRET déjà connu est enrichi,
            jamais recréé.
          </p>
        </>
      )}
      {stats && (
        <div role="status" className="success">
          <h3>Import terminé</h3>
          <div className="importSummary">
            {[
              ["newProspects", "Nouveaux prospects"],
              ["enriched", "Établissements connus enrichis"],
              ["reactivated", "Anciens clients réactivés"],
              ["reconquest", "Clients en reconquête"],
              ["missingSiret", "SIRET manquants"],
              ["invalidSiret", "SIRET invalides"],
              ["closed", "Établissements fermés"],
              ["incomplete", "Lignes incomplètes"],
            ].map(([key, label]) => (
              <div key={key}>
                <b>{stats[key]}</b>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!rows.length && !busy && (
        <Empty>
          Choisis un fichier pour vérifier les lignes avant de les importer.
        </Empty>
      )}
    </>
  );
}
